import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { MessageCircle, Plus, Users, UserMinus, Loader2, Trash2 } from 'lucide-react';
import { useAuth } from '@core/auth/useAuth';
import { cohortService, Cohort, CohortMember } from '@core/supabase/services/cohortService';
import { candidateService } from '@core/supabase/services/candidateService';
import { PageHeader } from '@shared/components/ui/PageHeader';
import { FullPageSpinner } from '@shared/components/ui/Spinner';
import type { Candidate } from '@core/types';

export function CollaboratePage() {
  const { user } = useAuth();
  const [cohorts, setCohorts] = useState<Cohort[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedCohort, setSelectedCohort] = useState<string | null>(null);
  const [members, setMembers] = useState<CohortMember[]>([]);
  const [loadingMembers, setLoadingMembers] = useState(false);

  // create form
  const [showForm, setShowForm] = useState(false);
  const [newName, setNewName] = useState('');
  const [newDesc, setNewDesc] = useState('');
  const [creating, setCreating] = useState(false);

  // add member search
  const [candidateSearch, setCandidateSearch] = useState('');
  const [searchResults, setSearchResults] = useState<Candidate[]>([]);
  const [searching, setSearching] = useState(false);
  const [boardUrlDraft, setBoardUrlDraft] = useState('');
  const [savingBoard, setSavingBoard] = useState(false);

  useEffect(() => {
    if (!user?.orgId) return;
    cohortService.list(user.orgId)
      .then(setCohorts)
      .finally(() => setLoading(false));
  }, [user?.orgId]);

  useEffect(() => {
    if (!selectedCohort) { setMembers([]); return; }
    setLoadingMembers(true);
    cohortService.listMembers(selectedCohort)
      .then(setMembers)
      .finally(() => setLoadingMembers(false));
  }, [selectedCohort]);

  const handleCreate = async () => {
    if (!user?.orgId || !newName.trim()) return;
    setCreating(true);
    try {
      const created = await cohortService.create(user.orgId, user.orgUserId ?? null, newName.trim(), newDesc || undefined);
      setCohorts((prev) => [created, ...prev]);
      setNewName('');
      setNewDesc('');
      setShowForm(false);
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Delete this cohort?')) return;
    await cohortService.delete(id);
    setCohorts((prev) => prev.filter((c) => c.id !== id));
    if (selectedCohort === id) setSelectedCohort(null);
  };

  const handleSearch = async (q: string) => {
    setCandidateSearch(q);
    if (!user?.orgId || q.trim().length < 2) { setSearchResults([]); return; }
    setSearching(true);
    try {
      const res = await candidateService.list(user.orgId, { search: q, pageSize: 8 });
      setSearchResults(Array.isArray(res) ? res : (res as { data: Candidate[] }).data);
    } finally {
      setSearching(false);
    }
  };

  const handleAddMember = async (candidate: Candidate) => {
    if (!selectedCohort) return;
    const member = await cohortService.addMember(selectedCohort, candidate.id);
    setMembers((prev) => {
      if (prev.find((m) => m.candidate_id === candidate.id)) return prev;
      return [...prev, member];
    });
    setCandidateSearch('');
    setSearchResults([]);
  };

  const handleRemoveMember = async (memberId: string) => {
    await cohortService.removeMember(memberId);
    setMembers((prev) => prev.filter((m) => m.id !== memberId));
  };

  const activeCohort = cohorts.find((c) => c.id === selectedCohort);

  useEffect(() => {
    setBoardUrlDraft(activeCohort?.board_url?.trim() ?? '');
  }, [activeCohort?.id, activeCohort?.board_url]);

  const saveBoardUrl = async () => {
    if (!selectedCohort) return;
    setSavingBoard(true);
    try {
      const url = boardUrlDraft.trim() || null;
      await cohortService.update(selectedCohort, { board_url: url });
      setCohorts((prev) => prev.map((c) => (c.id === selectedCohort ? { ...c, board_url: url } : c)));
    } finally {
      setSavingBoard(false);
    }
  };

  if (loading) return <FullPageSpinner />;

  return (
    <div>
      <PageHeader
        title="Collaborate"
        subtitle="Shared candidate cohorts for team sourcing"
        action={
          <button className="btn-primary" onClick={() => setShowForm(!showForm)}>
            <Plus className="w-4 h-4" /> {showForm ? 'Cancel' : 'New Cohort'}
          </button>
        }
      />

      {showForm && (
        <div className="card mb-6 space-y-3">
          <p className="text-xs font-semibold vscode-text-secondary uppercase tracking-wider">New cohort</p>
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <label className="label">Name *</label>
              <input
                className="input"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="e.g. Q2 Backend engineers"
                onKeyDown={(e) => { if (e.key === 'Enter') handleCreate(); }}
              />
            </div>
            <div>
              <label className="label">Description</label>
              <input
                className="input"
                value={newDesc}
                onChange={(e) => setNewDesc(e.target.value)}
                placeholder="Optional"
              />
            </div>
          </div>
          <button className="btn-primary" onClick={handleCreate} disabled={creating || !newName.trim()}>
            {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
            [Create Cohort]
          </button>
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Cohort list */}
        <div className="lg:col-span-1">
          <div className="card">
            <h2 className="text-sm font-semibold vscode-text-primary mb-3">Cohorts ({cohorts.length})</h2>
            {cohorts.length === 0 ? (
              <p className="text-sm vscode-text-tertiary">No cohorts yet — create one to group candidates for collaboration.</p>
            ) : (
              <div className="space-y-1">
                {cohorts.map((c) => (
                  <div
                    key={c.id}
                    className={`group flex items-center justify-between px-3 py-2 rounded cursor-pointer text-sm ${
                      selectedCohort === c.id ? 'bg-brand-500/10 text-brand-400' : 'hover:bg-brand-500/10 vscode-text-secondary'
                    }`}
                    onClick={() => setSelectedCohort(selectedCohort === c.id ? null : c.id)}
                  >
                    <span className="flex items-center gap-2 min-w-0">
                      <Users className="w-4 h-4 shrink-0" />
                      <span className="truncate">{c.name}</span>
                    </span>
                    <button
                      className="opacity-0 group-hover:opacity-100 vscode-text-tertiary hover:text-red-400 p-0.5 ml-1 shrink-0"
                      onClick={(e) => { e.stopPropagation(); handleDelete(c.id); }}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Cohort detail */}
        <div className="lg:col-span-2">
          <div className="card min-h-[200px]">
            {!selectedCohort ? (
              <div className="p-8 text-center">
                <MessageCircle className="w-12 h-12 vscode-text-tertiary mx-auto mb-3" />
                <p className="vscode-text-tertiary">Select a cohort to manage its candidates.</p>
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h2 className="text-sm font-semibold vscode-text-primary">{activeCohort?.name}</h2>
                    {activeCohort?.description && (
                      <p className="text-xs vscode-text-tertiary mt-0.5">{activeCohort.description}</p>
                    )}
                  </div>
                  <span className="text-xs vscode-text-tertiary">{members.length} candidate{members.length !== 1 ? 's' : ''}</span>
                </div>

                <div className="mb-4 p-3 rounded-lg border vscode-border bg-[color-mix(in_srgb,var(--vscode-bg-tertiary)_40%,transparent)] space-y-2">
                  <p className="text-xs font-semibold vscode-text-secondary uppercase tracking-wider">Shared board (optional)</p>
                  <p className="text-[11px] vscode-text-tertiary">
                    Link a Miro board, FigJam, Google Doc, or wiki for this cohort (ACT-040).
                  </p>
                  <div className="flex flex-col sm:flex-row gap-2">
                    <input
                      className="input flex-1 text-sm"
                      type="url"
                      placeholder="https://…"
                      value={boardUrlDraft}
                      onChange={(e) => setBoardUrlDraft(e.target.value)}
                    />
                    <button
                      type="button"
                      className="btn-primary text-sm shrink-0"
                      disabled={savingBoard}
                      onClick={() => void saveBoardUrl()}
                    >
                      {savingBoard ? 'Saving…' : 'Save link'}
                    </button>
                  </div>
                  {activeCohort?.board_url && /^https?:\/\//i.test(activeCohort.board_url) && (
                    <a
                      href={activeCohort.board_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-brand-400 hover:text-brand-300 inline-flex items-center gap-1"
                    >
                      [Open board →]
                    </a>
                  )}
                </div>

                {/* Candidate search to add members */}
                <div className="relative mb-4">
                  <input
                    className="input w-full"
                    placeholder="Search candidates to add…"
                    value={candidateSearch}
                    onChange={(e) => handleSearch(e.target.value)}
                    autoComplete="off"
                  />
                  {searching && (
                    <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin vscode-text-secondary" />
                  )}
                  {searchResults.length > 0 && (
                    <ul className="absolute z-10 mt-1 w-full rounded-lg border vscode-border bg-[var(--vscode-bg-elevated)] shadow-xl max-h-40 overflow-y-auto">
                      {searchResults.map((c) => (
                        <li
                          key={c.id}
                          className="flex items-center justify-between px-3 py-2 text-sm cursor-pointer hover:bg-brand-500/10"
                          onMouseDown={() => handleAddMember(c)}
                        >
                          <span className="vscode-text-primary">{c.full_name}</span>
                          <span className="text-xs vscode-text-tertiary">{c.current_title ?? ''}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                {/* Members list */}
                {loadingMembers ? (
                  <div className="animate-pulse h-24 bg-[var(--vscode-bg-tertiary)] rounded" />
                ) : members.length === 0 ? (
                  <p className="text-sm vscode-text-tertiary text-center py-4">
                    No candidates yet — search above to add some.
                  </p>
                ) : (
                  <ul className="divide-y divide-[var(--vscode-border-primary)]">
                    {members.map((m) => {
                      const cand = m.candidate;
                      return (
                        <li key={m.id} className="flex items-center justify-between py-2.5 gap-2">
                          <div className="min-w-0">
                            {cand ? (
                              <Link
                                to={`/candidates/${cand.id}`}
                                className="text-sm font-medium vscode-text-primary hover:text-brand-400"
                              >
                                {cand.full_name}
                              </Link>
                            ) : (
                              <span className="text-sm vscode-text-secondary">{m.candidate_id}</span>
                            )}
                            {cand?.current_title && (
                              <p className="text-xs vscode-text-tertiary truncate">{cand.current_title}</p>
                            )}
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            {m.fitment_score != null && (
                              <span className="text-xs text-brand-400 tabular-nums">{m.fitment_score}%</span>
                            )}
                            <button
                              className="p-1 vscode-text-tertiary hover:text-red-400"
                              onClick={() => handleRemoveMember(m.id)}
                              title="Remove from cohort"
                            >
                              <UserMinus className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
